import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { useMutation, useQuery, useQueryClient } from "@project/ui";
import { Button, formatShort } from "@project/ui";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { authClient } from "@apps/dashboard/lib/auth-client";
import { useActiveHouse } from "@apps/dashboard/features/houses/active-house-context";
import { MembersSplitConfigSection } from "@apps/dashboard/features/members/MembersSplitConfigSection";
import { useSyncHouseFromParams } from "@apps/dashboard/features/houses/use-sync-house-from-params";
import {
  createHouseInvitation,
  fetchHouseInvitations,
  fetchHouseMembers,
  homeExpenseQueryKeys,
  removeHouseMember,
  revokeHouseInvitation,
  updateHouseMember,
} from "@apps/dashboard/lib/home-expense-api";
import { ApiRequestError } from "@apps/dashboard/lib/api-fetch";
import { formatSplitBpsAsPercent } from "@apps/dashboard/lib/format-split-bps";
import { dashboardPaths } from "@apps/dashboard/lib/routes";
import { DashboardBackLink } from "@apps/dashboard/components/layout/DashboardBackLink";
import { DASHBOARD_PAGE_CONTAINER } from "@apps/dashboard/components/layout/dashboard-page-layout";
import { ErrorState } from "@apps/dashboard/components/states/ErrorState";
import { LoadingState } from "@apps/dashboard/components/states/LoadingState";

function memberRoleLabel(t: (k: string) => string, role: string): string {
  if (role === "owner") return t("dashboardUi.members.roleOwner");
  if (role === "admin") return t("dashboardUi.members.roleAdmin");
  if (role === "member") return t("dashboardUi.members.roleMember");
  return role;
}

export default function HouseMembersPage(): JSX.Element {
  const { houseId } = useParams<{ houseId: string }>();
  const { t, locale } = useI18n();
  const queryClient = useQueryClient();
  const { houses } = useActiveHouse();
  const { data: sessionData } = authClient.useSession();
  const currentUserId =
    sessionData?.user &&
    typeof sessionData.user === "object" &&
    "id" in sessionData.user &&
    typeof (sessionData.user as { id: unknown }).id === "string"
      ? (sessionData.user as { id: string }).id
      : undefined;
  const actorRoleFromHouse = houseId ? houses?.find((h) => h.id === houseId)?.role : undefined;
  useSyncHouseFromParams(houseId);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"member" | "admin">("member");
  const [inviteFormError, setInviteFormError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState(false);

  const membersQuery = useQuery({
    queryKey: houseId
      ? homeExpenseQueryKeys.houseMembers(locale, houseId)
      : ["houses", "members", "noop"],
    queryFn: () => {
      if (!houseId) throw new Error("missing houseId");
      return fetchHouseMembers(locale, houseId);
    },
    enabled: Boolean(houseId),
  });

  const invitationsQuery = useQuery({
    queryKey: houseId
      ? homeExpenseQueryKeys.houseInvitations(locale, houseId)
      : ["houses", "invitations", "noop"],
    queryFn: () => {
      if (!houseId) throw new Error("missing houseId");
      return fetchHouseInvitations(locale, houseId);
    },
    enabled: Boolean(houseId),
  });

  const inviteMutation = useMutation({
    mutationFn: () => {
      if (!houseId) throw new Error("missing houseId");
      return createHouseInvitation(locale, houseId, {
        email: inviteEmail.trim(),
        role: inviteRole,
      });
    },
    onSuccess: () => {
      setInviteEmail("");
      setInviteFormError(null);
      setInviteSuccess(true);
      toast.success(t("dashboardUi.members.inviteSent"));
      void queryClient.invalidateQueries({
        queryKey: ["houses", houseId, "invitations"],
      });
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof ApiRequestError
          ? err.message
          : t("dashboardUi.auth.errors.generic");
      setInviteFormError(msg);
      toast.error(msg);
    },
  });

  useEffect(() => {
    if (!inviteSuccess) return;
    const id = window.setTimeout(() => setInviteSuccess(false), 5000);
    return () => window.clearTimeout(id);
  }, [inviteSuccess]);

  const revokeMutation = useMutation({
    mutationFn: (invitationId: string) => {
      if (!houseId) throw new Error("missing houseId");
      return revokeHouseInvitation(locale, houseId, invitationId);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["houses", houseId, "invitations"],
      });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (targetUserId: string) => {
      if (!houseId) throw new Error("missing houseId");
      return removeHouseMember(locale, houseId, targetUserId);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["houses", houseId, "members"] });
      toast.success(t("dashboardUi.members.removeMemberSuccess"));
    },
  });

  const roleMutation = useMutation({
    mutationFn: (args: { targetUserId: string; role: "owner" | "admin" | "member" }) => {
      if (!houseId) throw new Error("missing houseId");
      return updateHouseMember(locale, houseId, args.targetUserId, { role: args.role });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["houses", houseId, "members"] });
      void queryClient.invalidateQueries({ queryKey: homeExpenseQueryKeys.houses(locale) });
      toast.success(t("dashboardUi.members.changeRoleSaved"));
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof ApiRequestError
          ? err.message
          : t("dashboardUi.auth.errors.generic");
      toast.error(msg);
    },
  });

  const members = membersQuery.data ?? [];
  const meMember = useMemo(
    () => (currentUserId ? members.find((m) => m.userId === currentUserId) : undefined),
    [members, currentUserId],
  );
  const actorRole = meMember?.role ?? actorRoleFromHouse;
  const canEditSplit = actorRole === "owner" || actorRole === "admin";
  const ownerCount = useMemo(() => members.filter((m) => m.role === "owner").length, [members]);

  if (!houseId) {
    return (
      <div className="mx-auto max-w-7xl p-6 lg:p-10">
        <ErrorState />
      </div>
    );
  }

  return (
    <div className={DASHBOARD_PAGE_CONTAINER}>
      <DashboardBackLink to={dashboardPaths.houses}>{t("dashboardUi.members.backToHouses")}</DashboardBackLink>
      <h1 className="font-headline text-headline-sm font-bold text-primary">{t("dashboardUi.members.pageTitle")}</h1>

      <section
        className="mt-8 rounded-2xl bg-surface-container-low p-6 outline outline-1 outline-outline-variant/15"
        data-tour="members-invite-section"
      >
        <h2 className="font-headline text-sm font-bold text-on-surface">
          {t("dashboardUi.members.inviteSectionTitle")}
        </h2>
        <p className="mt-2 text-body-md text-on-surface-variant">
          {canEditSplit ? t("dashboardUi.members.inviteHelp") : t("dashboardUi.members.inviteSectionAdminOnly")}
        </p>
        {canEditSplit ? (
        <>
        <form
          className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:items-end"
          onSubmit={(e) => {
            e.preventDefault();
            if (!inviteEmail.trim()) return;
            inviteMutation.mutate();
          }}
        >
          <div className="sm:col-span-2 lg:col-span-1">
            <label htmlFor="invite-email" className="text-label-md text-on-surface-variant">
              {t("dashboardUi.members.inviteEmailLabel")}
            </label>
            <input
              id="invite-email"
              type="email"
              autoComplete="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder={t("dashboardUi.members.inviteEmailPlaceholder")}
              className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-4 py-3 font-body text-body-md text-on-surface outline-none focus:border-primary"
            />
          </div>
          <div>
            <label htmlFor="invite-role" className="text-label-md text-on-surface-variant">
              {t("dashboardUi.members.roleLabel")}
            </label>
            <select
              id="invite-role"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as "member" | "admin")}
              className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-4 py-3 font-body text-body-md text-on-surface outline-none focus:border-primary"
            >
              <option value="member">{t("dashboardUi.members.roleMember")}</option>
              <option value="admin">{t("dashboardUi.members.roleAdmin")}</option>
            </select>
          </div>
          <Button
            type="submit"
            disabled={inviteMutation.isPending || !inviteEmail.trim()}
          >
            {inviteMutation.isPending
              ? t("dashboardUi.members.inviteSending")
              : t("dashboardUi.members.inviteSendCta")}
          </Button>
        </form>
        {inviteFormError ? (
          <p className="mt-3 text-body-md text-error" role="alert">
            {inviteFormError}
          </p>
        ) : null}
        {inviteSuccess ? (
          <p className="mt-3 text-body-md text-primary" role="status">
            {t("dashboardUi.members.inviteSent")}
          </p>
        ) : null}
        </>
        ) : null}
      </section>

      {invitationsQuery.isLoading ? (
        <div className="mt-6">
          <LoadingState />
        </div>
      ) : invitationsQuery.isError ? (
        <div className="mt-6">
          <ErrorState onRetry={() => void invitationsQuery.refetch()} />
        </div>
      ) : (invitationsQuery.data?.length ?? 0) > 0 ? (
        <section className="mt-8 rounded-2xl bg-surface-container-low p-6 outline outline-1 outline-outline-variant/15">
          <h2 className="font-headline text-sm font-bold text-on-surface">
            {t("dashboardUi.members.pendingInvitesTitle")}
          </h2>
          <ul className="mt-4 divide-y divide-outline-variant/15">
            {(invitationsQuery.data ?? []).map((inv) => (
              <li
                key={inv.id}
                className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0"
              >
                <div>
                  <p className="font-medium text-on-surface">{inv.email}</p>
                  <p className="text-label-md text-on-surface-variant">
                    {inv.expired
                      ? t("dashboardUi.members.pendingInviteExpiredBadge")
                      : t("dashboardUi.members.pendingInviteExpires", {
                          date: formatShort(inv.expiresAt),
                        })}{" "}
                    · {inv.role}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-error hover:text-error"
                  disabled={!canEditSplit || revokeMutation.isPending}
                  onClick={() => {
                    if (window.confirm(t("dashboardUi.members.revokeInviteConfirm"))) {
                      revokeMutation.mutate(inv.id);
                    }
                  }}
                >
                  {t("dashboardUi.members.revokeInviteCta")}
                </Button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {membersQuery.isLoading ? (
        <div className="mt-8">
          <LoadingState />
        </div>
      ) : membersQuery.isError ? (
        <div className="mt-8">
          <ErrorState onRetry={() => void membersQuery.refetch()} />
        </div>
      ) : (
        <>
          <div className="mt-8 overflow-x-auto rounded-2xl outline outline-1 outline-outline-variant/15">
            <table className="w-full min-w-[32rem] border-collapse text-left font-body text-body-md">
              <thead className="bg-surface-container">
                <tr>
                  <th className="p-4 font-label text-label-md text-on-surface-variant">
                    {t("dashboardUi.members.columnUser")}
                  </th>
                  <th className="p-4 font-label text-label-md text-on-surface-variant">
                    {t("dashboardUi.members.columnRole")}
                  </th>
                  <th className="p-4 font-label text-label-md text-on-surface-variant">
                    {t("dashboardUi.members.columnSplit")}
                  </th>
                  <th className="p-4 w-28" />
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr
                    key={m.id}
                    className="border-t border-outline-variant/15 bg-surface-container-lowest/50"
                  >
                    <td className="p-4">
                      <p className="font-medium text-on-surface">{m.user.name}</p>
                      <p className="text-label-md text-on-surface-variant">{m.user.email}</p>
                    </td>
                    <td className="p-4 text-on-surface">
                      {canEditSplit && (m.role !== "owner" || ownerCount > 1) ? (
                        <select
                          className="w-full min-w-[9rem] rounded-lg border border-outline-variant/30 bg-surface px-2 py-2 font-body text-body-sm text-on-surface outline-none focus:border-primary"
                          value={m.role}
                          disabled={roleMutation.isPending}
                          onChange={(e) => {
                            const v = e.target.value as "owner" | "admin" | "member";
                            if (v === m.role) return;
                            roleMutation.mutate({ targetUserId: m.userId, role: v });
                          }}
                          aria-label={t("dashboardUi.members.columnRole")}
                        >
                          <option value="owner">{t("dashboardUi.members.roleOwner")}</option>
                          <option value="admin">{t("dashboardUi.members.roleAdmin")}</option>
                          <option value="member">{t("dashboardUi.members.roleMember")}</option>
                        </select>
                      ) : (
                        memberRoleLabel(t, m.role)
                      )}
                    </td>
                    <td className="p-4 tabular-nums text-on-surface">
                      {m.defaultSplitBps != null && m.defaultSplitBps >= 0
                        ? formatSplitBpsAsPercent(m.defaultSplitBps, locale)
                        : "—"}
                    </td>
                    <td className="p-4">
                      {!canEditSplit ? (
                        <span className="text-label-md text-on-surface-variant">—</span>
                      ) : m.role === "owner" && ownerCount <= 1 ? (
                        <span
                          className="text-label-md text-on-surface-variant"
                          title={t("dashboardUi.members.removeOwnerNotAllowed")}
                        >
                          —
                        </span>
                      ) : (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-error hover:text-error"
                          disabled={removeMutation.isPending}
                          onClick={() => {
                            if (window.confirm(t("dashboardUi.members.removeConfirm"))) {
                              removeMutation.mutate(m.userId);
                            }
                          }}
                        >
                          {t("dashboardUi.members.removeCta")}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <MembersSplitConfigSection
            houseId={houseId}
            members={membersQuery.data ?? []}
            canEdit={canEditSplit}
            currentUserId={currentUserId}
          />
        </>
      )}
    </div>
  );
}
